import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Truck, PackageOpen } from 'lucide-react';

const TRANSPORT_IMG = "https://images.pexels.com/photos/7843987/pexels-photo-7843987.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MOVING_IMG = "https://images.unsplash.com/photo-1707407087163-7ab35bca9ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtb3ZpbmclMjBzZXJ2aWNlJTIwYm94ZXMlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzcxODI5ODM0fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: Truck,
    title: "Transport de marchandises",
    description: "Transport professionnel de marchandises pour particuliers et entreprises dans toute la Suisse. Flotte de camions et remorques adaptés à tous types de chargements.",
    image: TRANSPORT_IMG,
    alt: "Transport de marchandises professionnel en Suisse — cartons dans véhicule de livraison",
    features: ["Livraison dans toute la Suisse", "Flotte adaptée", "Suivi en temps réel"],
  },
  {
    icon: PackageOpen,
    title: "Déménagement",
    description: "Service de déménagement complet incluant emballage, montage/démontage de meubles et protection de vos biens. Pour particuliers et professionnels.",
    image: MOVING_IMG,
    alt: "Service de déménagement professionnel en Suisse romande — cartons et meubles emballés",
    features: ["Emballage professionnel", "Montage/démontage", "Transport sécurisé"],
  },
];

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToDevis = (e) => {
    e.preventDefault();
    const el = document.querySelector('#devis');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" ref={ref} className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="w-16 h-1 bg-[#FF0000] mb-6" />
          <h2 className="font-['Oswald'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white" data-testid="services-title">
            Nos Services
          </h2>
          <p className="text-white/50 mt-4 text-base md:text-lg max-w-xl">
            Des solutions de transport et déménagement sur mesure pour tous vos besoins.
          </p>
        </motion.div>

        {/* Service cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.2 }}
              className="service-card bg-[#121212] border border-[#2A2A2A] overflow-hidden group"
              data-testid={`service-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                <div className="absolute top-4 left-4 bg-[#FF0000] p-3">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="font-['Oswald'] text-2xl font-bold uppercase text-white mb-4" data-testid={`service-title-${index}`}>
                  {service.title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-8">
                  {service.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-3 text-white/70 text-sm">
                      <div className="w-1.5 h-1.5 bg-[#FF0000] rounded-full flex-shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>

                <a
                  href="#devis"
                  onClick={scrollToDevis}
                  className="inline-block bg-[#FF0000] hover:bg-[#D90000] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all hover:scale-105"
                  data-testid={`service-cta-${index}`}
                >
                  Demander un devis
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
